import {
  Autocomplete,
  Button,
  Input,
  NumberInput,
  Select,
  Textarea,
} from "@mantine/core";
import { DatePicker, Upload } from "antd";
import {
  IconAlertTriangle,
  IconBuildingStore,
  IconCalendar,
  IconCash,
  IconClipboardCheck,
  IconCreditCard,
  IconFileText,
  IconHash,
  IconMapPin,
  IconNotes,
  IconPackage,
  IconPaperclip,
  IconReceipt,
  IconShoppingCart,
  IconTruck,
  IconUser,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";

import { apiUserUpload } from "@/api";
import dayjs from "dayjs";
import { formatDeliveryLocation, toUnitPrice } from "@/lib/purchaseOrder";
import { notification } from "antd";
import { orpcClient } from "@/lib/orpc-client";
import { single, allOrThrow } from "@/lib/orpc-batch";
import { useData } from "@/utility/state/contextAPI";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

function EditPurchaseOrderPage() {
  const { t } = useTranslation();
  const [purchaseOrderData, setPurchaseOrderData] = useState({
    name: "",
    partnerId: "",
    purchaseDate: "",
    deliveryDate: "",
    orderStatus: "draft",
    paymentMethod: "",
    requiresInvoice: false,
    requiresIncomingQualityControl: false,
    shippingFeeCalculation: "freeShipping",
    company: localStorage.getItem("companyId"),
    deliveryMethod: "single",
    orderReminderDate: "",
    actualShippingFee: "",
    deliveryReceiver: "",
    deliveryLocation: "",
    remark: "",
    notice: "",
    attachment: "",
  });

  const [partnerData, setPartnerData] = useState<any[]>([]);
  const [fileUpload, setFileUpload] = useState<any[]>([]);
  const [uploadState, setUploadState] = useState(false);
  const [tempProductInput, setTempProductInput] = useState<any[]>([]);
  const [tempProductList, setTempProductList] = useState<any[]>([]);
  const [tempAmount, setTempAmount] = useState<any[]>([]);
  const [tempUnitPrice, setTempUnitPrice] = useState<any[]>([]);
  const [tempProductData, setTempProductData] = useState<any[]>([]);
  const [deleteProductList, setDeleteProductList] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const {
    modal: { contextOpenModal, contextCloseModal, contextSetModalContent },
  } = useData();
  const navigate = useNavigate();
  const { id } = useParams();
  const [api, contextHolder] = notification.useNotification();

  const {
    shippingFeeCalculation,
    deliveryMethod,
    requiresInvoice,
    requiresIncomingQualityControl,
    name,
    partnerId,
    purchaseDate,
    deliveryDate,
    orderStatus,
    paymentMethod,
    orderReminderDate,
    actualShippingFee,
    deliveryReceiver,
    deliveryLocation,
    remark,
    notice,
  } = purchaseOrderData || {};

  const handleDataChange = (name: string, value: any) => {
    setPurchaseOrderData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleOnOptionSubmit = (
    type: string,
    e: any,
    data: any[],
    index: number
  ) => {
    let selectedData: any;
    if (type === "product") {
      selectedData = data.filter((item: any) => item?.name === e)[0];
      setTempProductList((prev: any[]) => {
        const updatedList = [...prev];
        updatedList[index] = { ...updatedList[index], [type]: selectedData };
        return updatedList;
      });
    } else {
      let idBehindMatch = e.match(/ID:\s*([a-zA-Z0-9]+)/);
      let documentID = idBehindMatch ? idBehindMatch[1].trim() : null;

      setTempProductList((prev: any[]) => {
        const updatedList = [...prev];
        updatedList[index] = {
          ...updatedList[index],
          storageLocation: documentID,
        };
        return updatedList;
      });
    }
  };

  const handleDeleteProductList = (index: number, id: string) => {
    const updatedProductList = [...tempProductList];

    updatedProductList.splice(index, 1);

    setTempProductInput((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempAmount((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempUnitPrice((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempProductList(updatedProductList);

    if (id) {
      setDeleteProductList((prev) => [...prev, id]);
    }
  };

  const handleChangeImg = async (info: any) => {
    setUploadState(true);
    if (!uploadState) {
      const file = info.file.originFileObj;

      const formData = new FormData();
      formData.append("files", file, file.name);
      formData.append("name", info.file.name);

      const fileInfo = {
        alternativeText: localStorage.getItem("companyId"),
      };
      formData.append("fileInfo", JSON.stringify(fileInfo));

      let result = await apiUserUpload(formData);

      if (result[0]?.id) {
        setFileUpload((prev) => [...prev, result[0]]);
      } else {
        console.error(`apiUserUpload went wrong ${result}`);
      }
      setUploadState(false);
    }
  };

  const handleChangePartnerData = (
    value: string,
    data: any[],
    dataType: string
  ) => {
    let match = value.match(/ID:\s*([a-f0-9-]+)/);
    let idNumber = match ? match[1] : null;

    let selectedData = data.filter((e: any) => e?.id === idNumber)[0];

    handleDataChange(dataType, selectedData?.id);
  };

  const handleAddProductList = () => {
    setTempProductList((prev) => [...prev, []]);
  };

  // deliveryLocation 後端已改為純文字欄位，原本的縣市／行政區／郵遞區號／地址拆欄先保留註解
  // const handleAddressChange = (type: string, value: any) => {
  //   setPurchaseOrderData((prevData) => ({
  //     ...prevData,
  //     deliveryLocation: {
  //       ...prevData?.deliveryLocation,
  //       [type]: value,
  //     },
  //   }));
  // };

  const handleSubmit = async ({
    data,
    tempProductList,
    deleteProductList,
    submitForReview = false,
  }: {
    data: any;
    tempProductList: any[];
    deleteProductList: any[];
    submitForReview?: boolean;
  }) => {
    if (submitting) return;
    // Validate purchaseDate <= deliveryDate
    if (
      data.purchaseDate &&
      data.deliveryDate &&
      new Date(data.purchaseDate) > new Date(data.deliveryDate)
    ) {
      api.error({
        message: `Notification`,
        description: <div>{t("purchaseOrder.purchaseDateError")}</div>,
        placement: "topLeft",
        duration: 0,
      });
      return;
    }
    const {
      shippingFeeCalculation,
      deliveryMethod,
      requiresInvoice,
      requiresIncomingQualityControl,
      name,
      purchaseDate,
      deliveryDate,
      orderStatus,
      paymentMethod,
      orderReminderDate,
      actualShippingFee,
      deliveryReceiver,
      attachment,
      deliveryLocation,
      remark,
      notice,
    } = data;
    setSubmitting(true);
    try {
      let result = await single(
        orpcClient.purchaseOrder.update([
          {
            id: id!,
            shippingFeeCalculation,
            deliveryMethod,
            requiresInvoice,
            requiresIncomingQualityControl,
            name,
            purchaseDate: purchaseDate ? new Date(purchaseDate) : undefined,
            deliveryDate: deliveryDate ? new Date(deliveryDate) : undefined,
            paymentMethod,
            attachment:
              fileUpload?.length > 0
                ? JSON.stringify(fileUpload.map((file) => file.id))
                : undefined,
            orderReminderDate: orderReminderDate
              ? new Date(orderReminderDate)
              : undefined,
            actualShippingFee,
            deliveryReceiver,
            deliveryLocation,
            remark,
            notice,
          },
        ])
      );

      if (deleteProductList?.length) {
        await allOrThrow(
          orpcClient.purchaseOrderProduct.delete(
            deleteProductList.map((data) => ({ id: data }))
          )
        );
      }

      let editTempProductList: any[] = [];
      let newTempProductList: any[] = [];

      tempProductList?.map((t: any) => {
        if (t?.id) {
          editTempProductList.push(t);
        } else {
          newTempProductList.push(t);
        }
      });

      if (editTempProductList?.length) {
        await allOrThrow(
          orpcClient.purchaseOrderProduct.update(
            editTempProductList.map((data) => ({
              id: data.id,
              amount: Number(data?.amount),
              unitPrice: toUnitPrice(data?.unitPrice),
              productId: data?.product?.id,
              purchaseOrderId: (purchaseOrderData as any)?.id,
            }))
          )
        );
      }

      if (newTempProductList?.length) {
        await allOrThrow(
          orpcClient.purchaseOrderProduct.create(
            newTempProductList.map((data) => ({
              amount: Number(data?.amount),
              unitPrice: toUnitPrice(data?.unitPrice),
              productId: data?.product?.id,
              purchaseOrderId: (purchaseOrderData as any)?.id,
            }))
          )
        );
      }

      if (submitForReview) {
        await single(orpcClient.purchaseOrder.requestApproval([{ id: id! }]));
      }
    } catch (error: any) {
      console.error("Error post purchaseOrder data:", error);
      api.error({
        message: `Notification`,
        description: (
          <div>
            {error?.message ||
              (submitForReview
                ? t("purchaseOrder.submitFailed") || "採購單送審失敗"
                : t("purchaseOrder.saveFailed") || "儲存採購單失敗")}
          </div>
        ),
        placement: "topLeft",
        duration: 0,
      });
      return;
    } finally {
      setSubmitting(false);
    }
    navigate(`/hrMainPage/purchaseOrder/${id}`);
  };
  const handleDisplaySelectMembers = (data: any) => {
    return data?.map((t: any) => ({ value: t?.id, label: t?.name }));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await single(orpcClient.purchaseOrder.read([{ id: id! }]));

        const orderProducts = (result?.purchaseOrderProduct ||
          []) as any[];
        const products = orderProducts.length
          ? await allOrThrow(
              orpcClient.product.read(
                orderProducts.map((p: any) => ({ id: p.productId }))
              )
            )
          : [];
        const batchResults = orderProducts.map(
          (purchaseOrderProduct: any, index: number) => ({
            id: purchaseOrderProduct.id,
            amount: purchaseOrderProduct.amount,
            unitPrice: purchaseOrderProduct.unitPrice,
            createdAt: purchaseOrderProduct.createdAt,
            updatedAt: purchaseOrderProduct.updatedAt,
            product: products[index],
          })
        );

        setTempProductList(batchResults);

        setTempProductInput(
          batchResults?.map(({ product }: any) => product?.id || null)
        );

        setTempAmount(batchResults?.map((data) => data?.amount));

        setTempUnitPrice(batchResults?.map((data) => data?.unitPrice ?? ""));

        setPurchaseOrderData({
          ...result,
          partnerId: result?.partnerId || "",
          deliveryLocation: formatDeliveryLocation(result?.deliveryLocation),
          remark: result?.remark || "",
          notice: result?.notice || "",
        });
        setFileUpload(result?.attachment ? JSON.parse(result.attachment) : []);
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await orpcClient.partner.list({});
        setPartnerData(result);
      } catch (error) {
        console.error("Error fetching coop data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await orpcClient.product.list({});
        setTempProductData(result);
      } catch (error) {
        console.error("Error fetching apiUserGetProducts data:", error);
      }
    };

    fetchData();
  }, []);

  const renderControl = (label, control, Icon, required = false) => (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs text-gray-500">
        {Icon ? <Icon size={13} /> : null}
        {label}
        {required ? <span className="text-red-500">＊</span> : null}
      </label>
      {control}
    </div>
  );

  const renderSection = (title, Icon, children) => (
    <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
      <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
        <Icon size={16} className="text-blue-600" />
        <h3 className="text-sm font-medium text-gray-700">{title}</h3>
      </div>
      {children}
    </div>
  );

  return (
    <div className="min-h-screen p-8">
      {contextHolder}
      <div className="mx-auto max-w-4xl rounded-xl bg-white p-8 space-y-5">
        <div>
          <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
            {name || t("purchaseOrder.edit") || "編輯採購單"}
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {t("purchaseOrder.edit") || "編輯採購單"}
          </div>
        </div>

        {renderSection(
          t("purchaseOrder.basicInfo") || "基本資訊",
          IconShoppingCart,
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
            {renderControl(
              t("purchaseOrder.purchaseOrderName") || "採購單名稱",
              <Input
                leftSection={<IconShoppingCart size={16} />}
                value={name}
                onChange={(e) => handleDataChange("name", e.target.value)}
              />,
              IconShoppingCart
            )}
            {renderControl(
              t("purchaseOrder.purchaseDate") || "採購日期",
              <DatePicker
                style={{ width: "100%" }}
                value={purchaseDate ? dayjs(purchaseDate) : null}
                onChange={(e) =>
                  handleDataChange("purchaseDate", e.format("YYYY-MM-DD"))
                }
              />,
              IconCalendar
            )}
            {renderControl(
              t("purchaseOrder.partner") || "合作方",
              <Autocomplete
                disabled={true}
                value={partnerData?.find((t) => t?.id === partnerId)?.name || ""}
                leftSection={<IconBuildingStore size={16} />}
                data={partnerData?.map((t) => t?.name + ` (ID: ${t?.id})`)}
                onChange={(e) =>
                  handleChangePartnerData(e, partnerData, "partnerId")
                }
              />,
              IconBuildingStore,
              true
            )}
            {renderControl(
              t("purchaseOrder.paymentMethod") || "付款方式",
              <Input
                leftSection={<IconCreditCard size={16} />}
                value={paymentMethod}
                onChange={(e) =>
                  handleDataChange("paymentMethod", e.target.value)
                }
              />,
              IconCreditCard
            )}
            {renderControl(
              t("purchaseOrder.shippingFeeCalculation") || "運費計算方式",
              <Select
                value={
                  shippingFeeCalculation === "freeShipping"
                    ? t("purchaseOrder.freeShipping")
                    : t("purchaseOrder.shippingFeeRequired")
                }
                data={[
                  t("purchaseOrder.freeShipping"),
                  t("purchaseOrder.shippingFeeRequired"),
                ]}
                onChange={(value) =>
                  handleDataChange(
                    "shippingFeeCalculation",
                    value === t("purchaseOrder.shippingFeeRequired")
                      ? "shippingFeeRequired"
                      : "freeShipping"
                  )
                }
                leftSection={<IconTruck size={16} />}
              />,
              IconTruck
            )}
            {renderControl(
              t("purchaseOrder.actualShippingFee") || "實際運費",
              <NumberInput
                leftSection={<IconCash size={16} />}
                value={actualShippingFee}
                onChange={(e) => handleDataChange("actualShippingFee", e)}
              />,
              IconCash
            )}
            {renderControl(
              t("purchaseOrder.orderReminderDate") || "設定下單提醒",
              <DatePicker
                style={{ width: "100%" }}
                value={orderReminderDate ? dayjs(orderReminderDate) : null}
                onChange={(e) =>
                  handleDataChange("orderReminderDate", e.format("YYYY-MM-DD"))
                }
              />,
              IconCalendar
            )}
            {renderControl(
              t("purchaseOrder.requiresInvoice") || "是否開發票",
              <Select
                value={
                  requiresInvoice
                    ? t("purchaseOrder.yes")
                    : t("purchaseOrder.no")
                }
                data={[t("purchaseOrder.yes"), t("purchaseOrder.no")]}
                onChange={(value) =>
                  handleDataChange(
                    "requiresInvoice",
                    value === t("purchaseOrder.yes") ? true : false
                  )
                }
                leftSection={<IconReceipt size={16} />}
              />,
              IconReceipt
            )}
            {renderControl(
              t("purchaseOrder.requiresIncomingQualityControl") ||
                "是否要進料檢",
              <Select
                value={
                  requiresIncomingQualityControl
                    ? t("purchaseOrder.yes")
                    : t("purchaseOrder.no")
                }
                data={[t("purchaseOrder.yes"), t("purchaseOrder.no")]}
                onChange={(value) =>
                  handleDataChange(
                    "requiresIncomingQualityControl",
                    value === t("purchaseOrder.yes") ? true : false
                  )
                }
                leftSection={<IconClipboardCheck size={16} />}
              />,
              IconClipboardCheck
            )}
          </div>
        )}

        {renderSection(
          t("purchaseOrder.incomingQCItems") || "進料檢項目",
          IconPaperclip,
          <div className="space-y-3 p-6">
            <Upload
              name="avatar"
              showUploadList={false}
              onChange={handleChangeImg}
            >
              <Button variant="default" radius="md" size="sm">
                {t("purchaseOrder.addFile") || "新增檔案"}
              </Button>
            </Upload>
            {fileUpload?.length ? (
              <div className="overflow-hidden rounded-lg border border-gray-200">
                {fileUpload?.map((uploadedFile) => (
                  <div
                    key={uploadedFile?.id}
                    className="flex items-center justify-between border-b border-gray-100 px-4 py-2 last:border-b-0"
                  >
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <IconFileText size={15} className="text-gray-400" />
                      {uploadedFile?.name}
                    </div>
                    <button
                      type="button"
                      className="text-red-500 hover:text-red-700"
                      onClick={() =>
                        setFileUpload((prev) =>
                          prev.filter((file) => file.id !== uploadedFile?.id)
                        )
                      }
                    >
                      ❌
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconPackage size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("purchaseOrder.product") || "商品"}
            </h3>
          </div>
          <div className="space-y-4 p-6">
            {tempProductList.length ? (
              tempProductList.map((productRow, i) => (
                <div
                  key={productRow?.id || i}
                  className="grid grid-cols-1 items-end gap-4 rounded-lg border border-gray-200 p-4 md:grid-cols-[2fr_1fr_1fr_auto]"
                >
                  {renderControl(
                    t("purchaseOrder.productName") || "商品名稱",
                    <Select
                      searchable
                      nothingFoundMessage="查無資料"
                      leftSection={<IconPackage size={16} />}
                      data={handleDisplaySelectMembers(tempProductData)}
                      value={tempProductInput[i] || null}
                      onChange={(value) => {
                        setTempProductInput((prev) => {
                          const updated = [...prev];
                          updated[i] = value;
                          return updated;
                        });
                        const selectedData = tempProductData.find(
                          (p: any) => p?.id === value
                        );
                        setTempProductList((prev: any[]) => {
                          const updated = [...prev];
                          updated[i] = {
                            ...updated[i],
                            product: selectedData,
                          };
                          return updated;
                        });
                      }}
                    />,
                    IconPackage
                  )}
                  {renderControl(
                    t("purchaseOrder.quantity") || "數量",
                    <NumberInput
                      leftSection={<IconHash size={16} />}
                      value={tempAmount[i] || ""}
                      onChange={(value) => {
                        setTempProductList((prev) => {
                          const updatedList = [...prev];
                          updatedList[i] = {
                            ...updatedList[i],
                            amount: value,
                          };
                          return updatedList;
                        });

                        setTempAmount((prev) => {
                          const updated = [...prev];
                          updated[i] = value;
                          return updated;
                        });
                      }}
                      min={0}
                    />,
                    IconHash
                  )}
                  {renderControl(
                    t("purchaseOrder.unitPrice") || "單價",
                    <NumberInput
                      leftSection={<IconCash size={16} />}
                      value={tempUnitPrice[i] ?? ""}
                      decimalScale={2}
                      onChange={(value) => {
                        setTempProductList((prev) => {
                          const updatedList = [...prev];
                          updatedList[i] = {
                            ...updatedList[i],
                            unitPrice: value,
                          };
                          return updatedList;
                        });

                        setTempUnitPrice((prev) => {
                          const updated = [...prev];
                          updated[i] = value;
                          return updated;
                        });
                      }}
                      min={0}
                    />,
                    IconCash
                  )}
                  <Button
                    variant="filled"
                    color="red"
                    radius="md"
                    onClick={() => handleDeleteProductList(i, productRow?.id)}
                  >
                    {t("purchaseOrder.delete") || "刪除"}
                  </Button>
                </div>
              ))
            ) : (
              <div className="text-center text-sm text-gray-400">
                {t("purchaseOrder.addProductRow") || "增加商品列"}
              </div>
            )}
            <div className="flex justify-center pt-2">
              <Button
                variant="default"
                radius="md"
                size="sm"
                onClick={() => handleAddProductList()}
              >
                {t("purchaseOrder.addProductRow") || "增加商品列"}
              </Button>
            </div>
          </div>
        </div>

        {renderSection(
          t("purchaseOrder.deliverySettings") || "交貨設定",
          IconTruck,
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
            {renderControl(
              t("purchaseOrder.deliveryMethodTitle") || "交貨方式",
              <Select
                value={
                  deliveryMethod === "single"
                    ? t("purchaseOrder.singleDelivery")
                    : t("purchaseOrder.partialDelivery")
                }
                data={[
                  t("purchaseOrder.singleDelivery"),
                  t("purchaseOrder.partialDelivery"),
                ]}
                onChange={(value) =>
                  handleDataChange(
                    "deliveryMethod",
                    value === t("purchaseOrder.singleDelivery")
                      ? "single"
                      : "partial"
                  )
                }
                leftSection={<IconTruck size={16} />}
              />,
              IconTruck,
              true
            )}
            {renderControl(
              t("purchaseOrder.deliveryDate") || "交貨日期",
              <DatePicker
                style={{ width: "100%" }}
                value={deliveryDate ? dayjs(deliveryDate) : null}
                onChange={(e) =>
                  handleDataChange("deliveryDate", e.format("YYYY-MM-DD"))
                }
              />,
              IconCalendar,
              true
            )}
            {renderControl(
              t("purchaseOrder.deliveryReceiver") || "交貨簽收人員",
              <Input
                leftSection={<IconUser size={16} />}
                value={deliveryReceiver}
                onChange={(e) =>
                  handleDataChange("deliveryReceiver", e.target.value)
                }
              />,
              IconUser
            )}
          </div>
        )}

        {renderSection(
          t("purchaseOrder.deliveryLocationTitle") || "交貨地點",
          IconMapPin,
          <div className="p-6">
            {renderControl(
              t("purchaseOrder.deliveryLocationTitle") || "交貨地點",
              <Input
                leftSection={<IconMapPin size={16} />}
                placeholder={
                  t("purchaseOrder.deliveryLocationPlaceholder") ||
                  "請輸入交貨地點"
                }
                value={deliveryLocation || ""}
                onChange={(e) =>
                  handleDataChange("deliveryLocation", e.target.value)
                }
              />,
              IconMapPin
            )}
          </div>
          // 後端 deliveryLocation 已由 FullAddress jsonb 改為 text，以下拆欄輸入先保留註解
          // <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2 lg:grid-cols-4">
          //   {renderControl(t("purchaseOrder.city"), <Input ... />, IconMapPin)}
          //   {renderControl(t("purchaseOrder.district"), <Input ... />, IconMapPin)}
          //   {renderControl(t("purchaseOrder.postalCode"), <InputNumber ... />, IconHash)}
          //   {renderControl(t("purchaseOrder.address"), <Input ... />, IconHome)}
          // </div>
        )}

        {renderSection(
          t("purchaseOrder.remarkNoticeTitle") || "備註與注意事項",
          IconNotes,
          <div className="grid grid-cols-1 gap-4 p-6">
            {renderControl(
              t("purchaseOrder.remark") || "備註",
              <Textarea
                autosize
                minRows={3}
                placeholder={
                  t("purchaseOrder.remarkPlaceholder") || "請輸入備註"
                }
                value={remark || ""}
                onChange={(e) => handleDataChange("remark", e.target.value)}
              />,
              IconNotes
            )}
            {renderControl(
              t("purchaseOrder.notice") || "注意事項",
              <Textarea
                autosize
                minRows={3}
                placeholder={
                  t("purchaseOrder.noticePlaceholder") || "請輸入注意事項"
                }
                value={notice || ""}
                onChange={(e) => handleDataChange("notice", e.target.value)}
              />,
              IconAlertTriangle
            )}
          </div>
        )}

        <div className="flex gap-4">
          <Button
            variant="default"
            style={{ flex: 1 }}
            radius="md"
            disabled={submitting}
            onClick={() => navigate(`/hrMainPage/purchaseOrder/${id}`)}
          >
            {t("purchaseOrder.cancel") || "取消"}
          </Button>
          <Button
            variant="default"
            style={{ flex: 1 }}
            radius="md"
            loading={submitting}
            onClick={() =>
              handleSubmit({
                data: {
                  ...purchaseOrderData,
                },
                deleteProductList,
                tempProductList,
              })
            }
          >
            {t("purchaseOrder.save") || "儲存"}
          </Button>
          <Button
            variant="filled"
            color="gray"
            style={{ flex: 1 }}
            radius="md"
            loading={submitting}
            onClick={() =>
              handleSubmit({
                data: {
                  ...purchaseOrderData,
                },
                deleteProductList,
                tempProductList,
                submitForReview: true,
              })
            }
          >
            {t("purchaseOrder.createAndSubmit") || "建立並送審"}
          </Button>
        </div>
      </div>
    </div>
  );
}
export default EditPurchaseOrderPage;
